import {isAfter, isBefore, startOfDay} from 'date-fns';
import {match} from 'ts-pattern';

import {isNotNil} from 'ramda';

import {parseDate} from 'shared/web';

export type VehicleFinancingTemplateStatus = 'ACTIVE' | 'PLANNED' | 'EXPIRED' | 'ARCHIVED';

interface VehicleFinancingTemplateStatusInput {
  archived: boolean;
  validFrom: string | null;
  validTo: string | null;
}

/**
 * Derives the vehicle financing template status on the FE because the `sales_context` API
 * does not expose a status field (see design doc §1,
 * `docs/plans/2026-07-23-vehicle-financing-templates-design.md`). This may change if the
 * backend starts returning a computed status — this util should then be removed in favor
 * of reading the response field directly.
 *
 * `today` is injectable for deterministic testing and defaults to the current date.
 */
export function getVehicleFinancingTemplateStatus(
  template: VehicleFinancingTemplateStatusInput,
  today: Date = new Date()
): VehicleFinancingTemplateStatus {
  const normalizedToday = startOfDay(today);

  return match(template)
    .with({archived: true}, () => 'ARCHIVED' as const)
    .when(
      ({validFrom}) =>
        isNotNil(validFrom) && isAfter(startOfDay(parseDate(validFrom)), normalizedToday),
      () => 'PLANNED' as const
    )
    .when(
      ({validTo}) => isNotNil(validTo) && isBefore(startOfDay(parseDate(validTo)), normalizedToday),
      () => 'EXPIRED' as const
    )
    .otherwise(() => 'ACTIVE' as const);
}
